import { useMemo } from 'react';
import { DetectionSummaryInsights } from '../../types';
import {
  DetectionCount,
  ScalingStrategy,
  CappedLinearScalingStrategy,
} from '../../../../shared/types';
import { i18n } from '@investigator/shared/localization';

export interface BarChartWidthConfig {
  widthPx: number;
  totalDetections: number;
}

/**
 * Branded type for pixel widths to ensure valid range
 */
type WidthPx = number & { readonly __brand: 'WidthPx' };

/**
 * Type-safe detection counts array (readonly and sorted)
 */
type SortedDetectionCounts = readonly DetectionCount[];

/**
 * Configuration structure type
 */
interface ScalingConfigStructure {
  readonly widths: {
    readonly min: number;
    readonly outlierMin: number;
    readonly max: number;
    readonly base: number;
  };
  readonly thresholds: {
    readonly linear: number;
    readonly outlier: number;
    readonly minRatio: number;
    readonly smallValueRatio: number;
  };
  readonly spacing: {
    readonly outlierReserved: number;
  };
}

/**
 * Configuration for bar chart width scaling behavior
 */
export const SCALING_CONFIG: ScalingConfigStructure = {
  widths: {
    min: 30,
    outlierMin: 20,
    max: 300,
    base: 125,
  },
  thresholds: {
    linear: 30, // Use linear scaling if max detections ≤ this
    outlier: 0.3, // Values < 30% of second smallest are considered outliers
    minRatio: 0.2, // Smallest bar should be at least 20% of largest
    smallValueRatio: 0.1, // Apply MIN_RATIO constraint when value < 10% of max
  },
  spacing: {
    outlierReserved: 30, // Pixels reserved above outlier minimum for majority range
  },
} as const;

/**
 * Cap value for capped linear scaling
 * Entities with detections above this value will be treated as having this many
 */
export const CAPPED_LINEAR_MAX = 30;

/**
 * Minimum width for single detection (count=1) entities
 * Kept small to visually distinguish from higher counts
 */
const SINGLE_DETECTION_WIDTH = 20;

/**
 * Type guard to ensure a number is a valid detection count (non-negative)
 */
const isValidDetectionCount = (value: number): value is DetectionCount => {
  return Number.isFinite(value) && value >= 0;
};

/**
 * Safely cast a number to DetectionCount with validation
 */
const toDetectionCount = (value: number): DetectionCount => {
  if (!isValidDetectionCount(value)) {
    const { invalidDetectionCount } =
      i18n.pages.entities.severityBreakdownBarChart.errors;
    throw new Error(invalidDetectionCount.replace('{0}', String(value)));
  }
  return value as DetectionCount;
};

/**
 * Type guard to ensure width is within valid pixel range
 */
const isValidWidth = (value: number): value is WidthPx => {
  const { outlierMin, max } = SCALING_CONFIG.widths;
  return Number.isFinite(value) && value >= outlierMin && value <= max;
};

/**
 * Safely cast a number to WidthPx with validation and clamping
 */
const toWidthPx = (value: number): WidthPx => {
  const { outlierMin, max } = SCALING_CONFIG.widths;
  const clamped = Math.max(outlierMin, Math.min(max, value));

  if (!isValidWidth(clamped)) {
    const { invalidWidth } =
      i18n.pages.entities.severityBreakdownBarChart.errors;
    throw new Error(
      invalidWidth
        .replace('{0}', String(value))
        .replace('{1}', String(outlierMin))
        .replace('{2}', String(max)),
    );
  }
  return clamped as WidthPx;
};

/**
 * Extract total detection count from an entity's severity level counts
 * @returns A validated non-negative detection count
 */
const getTotalDetections = (
  entity: DetectionSummaryInsights,
): DetectionCount => {
  const counts = entity.detection_counts_by_severity_level ?? {
    low: 0,
    medium: 0,
    high: 0,
  };

  const total = (counts.low ?? 0) + (counts.medium ?? 0) + (counts.high ?? 0);
  return toDetectionCount(Math.max(0, total)); // Ensure non-negative
};

/**
 * Detect if the dataset contains outliers (values much smaller than the rest)
 * An outlier is defined as a value < 30% of the second smallest value
 */
const hasOutliers = (sortedCounts: SortedDetectionCounts): boolean => {
  if (sortedCounts.length <= 2) return false;

  const smallest = sortedCounts[0];
  const secondSmallest = sortedCounts[1];

  return smallest < secondSmallest * SCALING_CONFIG.thresholds.outlier;
};

/**
 * Check if a specific count is an outlier
 */
const isOutlier = (
  count: DetectionCount,
  sortedCounts: SortedDetectionCounts,
): boolean => {
  if (sortedCounts.length <= 1) return false;

  const secondSmallest = sortedCounts[1];
  return count < secondSmallest * SCALING_CONFIG.thresholds.outlier;
};

/**
 * Calculate width using linear scaling
 * Used for small detection counts where linear representation is clearer
 */
const calculateLinearWidth = (
  count: DetectionCount,
  maxCount: DetectionCount,
): WidthPx => {
  const { min, max } = SCALING_CONFIG.widths;

  if (maxCount === 0) return toWidthPx(min);

  const ratio = count / maxCount;
  const width = min + ratio * (max - min);

  return toWidthPx(Math.round(width * 10) / 10);
};

/**
 * Calculate width using capped linear scaling
 * Linear scaling with a maximum cap - values above cap are treated as equal to cap
 * This provides intuitive linear relationships while handling extreme outliers
 */
const calculateCappedLinearWidth = (
  count: DetectionCount,
  strategy: CappedLinearScalingStrategy,
): WidthPx => {
  const { min, max } = SCALING_CONFIG.widths;
  const { cap } = strategy;

  if (cap === 0) return toWidthPx(min);

  // Cap the count at the maximum cap value
  const cappedCount = Math.min(count, cap);
  const ratio = cappedCount / cap;
  const width = min + ratio * (max - min);

  return toWidthPx(Math.round(width * 10) / 10);
};

/**
 * Calculate width using logarithmic (log10) scaling
 * Used for larger counts to compress wide variations and improve visual balance
 * Log10 provides better compression than square root for wide ranges
 */
const calculateLogarithmicWidth = (
  count: DetectionCount,
  minCount: DetectionCount,
  maxCount: DetectionCount,
  hasOutliersInData: boolean,
): WidthPx => {
  const { min, outlierMin, max } = SCALING_CONFIG.widths;
  const { minRatio, smallValueRatio } = SCALING_CONFIG.thresholds;
  const { outlierReserved } = SCALING_CONFIG.spacing;

  // Log10 scaling to compress the range (add 1 to avoid log(0))
  const logMin = Math.log10(Math.max(1, minCount));
  const logMax = Math.log10(Math.max(1, maxCount));
  const logCurrent = Math.log10(Math.max(1, count));

  // Normalize to 0-1 range
  const normalizedRatio =
    logMax === logMin ? 1 : (logCurrent - logMin) / (logMax - logMin);

  // Apply minimum ratio constraint only for very small values relative to max
  const ratioToMax = maxCount === 0 ? 1 : count / maxCount;
  const shouldApplyMinRatio = ratioToMax < smallValueRatio;

  // Special case: When outliers exist AND this value equals minCount (the effectiveMin),
  // don't apply MIN_RATIO boost. This prevents the effectiveMin from being artificially
  // inflated above nearby values, which would cause visual inversions.
  // Example: [1, 8, 10, 13, 100] where 1 is outlier and 8 is effectiveMin
  // Without this check, 8 would get MIN_RATIO boost making it wider than 10 or 13
  const isEffectiveMinWithOutliers =
    hasOutliersInData && count === minCount && normalizedRatio < 0.05;

  const adjustedRatio =
    shouldApplyMinRatio && !isEffectiveMinWithOutliers
      ? Math.max(normalizedRatio, minRatio)
      : normalizedRatio;

  // Scale to pixel range, reserving space for outliers at the bottom
  const availableRange = hasOutliersInData
    ? max - outlierMin - outlierReserved
    : max - min;
  const baseWidth = hasOutliersInData ? outlierMin + outlierReserved : min;

  const width = baseWidth + adjustedRatio * availableRange;
  return toWidthPx(Math.round(width * 10) / 10);
};

/**
 * Calculate width for a single entity based on its detection count
 */
const calculateWidth = (
  count: DetectionCount,
  minCount: DetectionCount,
  sortedCounts: SortedDetectionCounts,
  strategy: ScalingStrategy,
): WidthPx => {
  // Special case: single detection gets a distinctly smaller width
  if (count === 1) {
    return toWidthPx(SINGLE_DETECTION_WIDTH);
  }

  const hasOutliersInData = hasOutliers(sortedCounts);
  const { outlierMin } = SCALING_CONFIG.widths;

  // If this is an outlier, give it the smaller minimum width
  if (hasOutliersInData && isOutlier(count, sortedCounts)) {
    return toWidthPx(outlierMin);
  }

  // Use linear scaling for small counts
  if (strategy.type === 'linear') {
    return calculateLinearWidth(count, strategy.maxCount);
  }

  // Use capped linear scaling
  if (strategy.type === 'capped-linear') {
    return calculateCappedLinearWidth(count, strategy);
  }

  // For larger counts, use logarithmic scaling
  // Use effective min for better scaling when outliers exist
  const effectiveMin = hasOutliersInData ? sortedCounts[1] : minCount;
  return calculateLogarithmicWidth(
    count,
    effectiveMin,
    strategy.maxCount,
    hasOutliersInData,
  );
};

/**
 * Determine the appropriate scaling strategy based on max detections
 *
 * Strategy selection:
 * - Linear: For small counts (≤30) where proportions are intuitive
 * - Logarithmic: For large counts (>30) to compress wide variations (DEFAULT)
 * - Capped Linear: Uncomment to use linear scaling with cap at 50
 */
const determineScalingStrategy = (
  _minCount: DetectionCount,
  maxCount: DetectionCount,
): ScalingStrategy => {
  const useLinear = maxCount <= SCALING_CONFIG.thresholds.linear;

  if (useLinear) {
    return { type: 'linear', maxCount };
  }

  // Uncomment to use logarithmic scaling for wide ranges
  // return { type: 'logarithmic', minCount, maxCount };

  return {
    type: 'capped-linear',
    cap: toDetectionCount(CAPPED_LINEAR_MAX),
    maxCount,
  };
};

/**
 * Create a sorted, readonly array of detection counts
 */
const createSortedCounts = (
  counts: readonly DetectionCount[],
): SortedDetectionCounts => {
  return counts.slice().sort((a, b) => a - b) as SortedDetectionCounts;
};

/**
 * Default global scale for consistent bar chart widths across page sizes
 * Range of 1-200 covers typical entity detection counts while providing
 * good visual distinction for small values
 */
const DEFAULT_GLOBAL_SCALE = {
  minCount: 1 as DetectionCount,
  maxCount: 200 as DetectionCount,
} as const;

/**
 * Calculate dynamic width for bar charts based on detection counts
 *
 * This hook implements an adaptive scaling strategy:
 * - Uses a fixed global scale (1-200) for consistent widths across page sizes
 * - Linear scaling for small counts (≤30 detections) for clear proportional representation
 * - Logarithmic (log10) scaling for larger counts to compress wide variations
 * - Special handling for outliers (values < 30% of second smallest) to prevent distortion
 * - Enforces minimum widths to ensure clickability and usability
 *
 * @param tableData - Array of entity detection summaries for the current page
 * @returns Map of entity index to width configuration
 *
 * @example
 * const widths = useBarChartWidthScaling(tableData);
 * // widths[0] = { widthPx: 150, totalDetections: 75 }
 * // widths[1] = { widthPx: 45, totalDetections: 6 }
 */
export const useBarChartWidthScaling = (
  tableData: readonly DetectionSummaryInsights[],
): Readonly<Record<number, BarChartWidthConfig>> => {
  return useMemo(() => {
    // Handle empty or invalid data
    if (!tableData || tableData.length === 0) {
      return {};
    }

    // Extract total detections for each entity (type-safe)
    const detectionCounts: readonly DetectionCount[] =
      tableData.map(getTotalDetections);

    // Use fixed global scale (1-200) for consistent scaling across page sizes
    const minDetections = DEFAULT_GLOBAL_SCALE.minCount;
    const maxDetections = DEFAULT_GLOBAL_SCALE.maxCount;

    // If all entities have the same count or only one entity, use base width
    if (minDetections === maxDetections || tableData.length === 1) {
      return tableData.reduce(
        (acc, _, index) => {
          acc[index] = {
            widthPx: SCALING_CONFIG.widths.base,
            totalDetections: detectionCounts[index],
          };
          return acc;
        },
        {} as Record<number, BarChartWidthConfig>,
      );
    }

    // Determine scaling strategy based on max detections
    const strategy = determineScalingStrategy(minDetections, maxDetections);

    // Sort counts for outlier detection (readonly, sorted)
    const sortedCounts = createSortedCounts(detectionCounts);

    // Calculate scaled widths
    const widthConfigs: Record<number, BarChartWidthConfig> = {};

    detectionCounts.forEach((count, index) => {
      const width = calculateWidth(
        count,
        minDetections,
        sortedCounts,
        strategy,
      );

      widthConfigs[index] = {
        widthPx: width,
        totalDetections: count,
      };
    });

    return widthConfigs;
  }, [tableData]);
};
